/**
 * PageHelpBubble — contextual help chip shown in the page title bar.
 *
 * Rendered once in DashboardLayout. It reads the current route and
 * navigates to the Support panel (/chat) with the relevant tutorial
 * automatically highlighted in the Video Tutorials tab.
 *
 * Behaviour:
 *  - Appears after a 1.5s delay
 *  - Clicking navigates to /chat and highlights the mapped tutorial
 *  - The × button dismisses it for the current session (sessionStorage)
 *  - Does not render on routes with no configured tutorial
 */

import React, { useState, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { getTutorialForRoute, getTutorialById } from "../tutorials/TutorialTree";

const DISMISS_KEY = (path) => `help_dismissed_${path}`;

export default function PageHelpBubble() {
  const location  = useLocation();
  const history   = useHistory();
  const [visible,   setVisible]   = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const path       = location.pathname;
  const tutorialId = getTutorialForRoute(path);
  const tutorial   = tutorialId ? getTutorialById(tutorialId) : null;

  /* reset dismiss state whenever route changes */
  useEffect(() => {
    if (!tutorial) return;
    const wasDismissed = sessionStorage.getItem(DISMISS_KEY(path)) === "1";
    setDismissed(wasDismissed);
    setVisible(false);

    if (wasDismissed) return;

    const t = setTimeout(() => setVisible(true), 1500);
    return () => clearTimeout(t);
  }, [path, tutorial]);

  if (!tutorial || dismissed) return null;

  const handleClick = () => {
    history.push("/tutorials", { tab: "tutorials", tutorialId });
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    sessionStorage.setItem(DISMISS_KEY(path), "1");
    setDismissed(true);
  };

  /* pick accent color from series */
  const SERIES_COLORS = {
    "Onboarding":             "#545cd8",
    "Strategy":               "#3ac47d",
    "Rules":                  "#f7b924",
    "Monitoring & Execution": "#30b1ff",
    "Settings":               "#d92550",
  };
  const accent = SERIES_COLORS[tutorial.series] || "#545cd8";

  return (
    <div
      style={{
        position:   "fixed",
        top:        68,
        right:      24,
        zIndex:     1050,
        opacity:    visible ? 1 : 0,
        transform:  visible ? "translateY(0)" : "translateY(-6px)",
        transition: "opacity 0.35s ease, transform 0.35s ease",
        pointerEvents: visible ? "auto" : "none",
      }}
    >
      <div
        onClick={handleClick}
        style={{
          display:      "flex",
          alignItems:   "center",
          gap:          6,
          background:   "#fff",
          border:       `1px solid ${accent}44`,
          borderRadius: 20,
          padding:      "5px 10px 5px 12px",
          boxShadow:    `0 2px 10px ${accent}22`,
          cursor:       "pointer",
          userSelect:   "none",
        }}
      >
        <span style={{ fontSize: 13, lineHeight: 1 }}>📹</span>

        <span style={{
          fontSize:   12,
          fontWeight: 500,
          color:      accent,
          whiteSpace: "nowrap",
          maxWidth:   240,
          overflow:   "hidden",
          textOverflow: "ellipsis",
        }}>
          Watch: {tutorial.title}
        </span>

        <button
          onClick={handleDismiss}
          title="Dismiss"
          style={{
            background: "none", border: "none",
            padding: "0 2px", marginLeft: 2,
            cursor: "pointer", color: "#bbb",
            fontSize: 14, lineHeight: 1,
            display: "flex", alignItems: "center",
          }}
          onMouseEnter={e => e.currentTarget.style.color = "#888"}
          onMouseLeave={e => e.currentTarget.style.color = "#bbb"}
        >
          ×
        </button>
      </div>
    </div>
  );
}
